import { rumService } from '../services/rum';
import logger from './logger';

/**
 * Generates a deterministic hash number from a string.
 * Uses a polynomial rolling hash algorithm with overflow protection.
 * Same input always produces the same output.
 *
 * @param str - The string to hash
 * @returns a positive integer hash value
 */
function hashString(str: string): number {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    // (hash << 5) shifts left by 5 bits, equivalent to multiplying by 32
    // Subtracting hash gives us: 32 * hash - hash = 31 * hash
    // Add the character's numeric value
    // | 0 forces the result to a 32-bit signed integer, preventing overflow
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }

  // Return absolute value to ensure positive number
  return Math.abs(hash);
}

/**
 * Determines if a feature should be used based on user ID and rollout percentage.
 *
 * @param userId - The user identifier
 * @param rolloutPercent - The desired rollout percentage (0-100)
 * @returns true if the feature should be used, false otherwise
 */
function shouldUseFeature(userId: string, rolloutPercent: number): boolean {
  if (rolloutPercent === 100) return true;
  if (!userId || rolloutPercent === 0) return false;
  return hashString(userId) % 100 < rolloutPercent;
}

/**
 * Determines if a custom domain should be used based on rollout percentage.
 *
 * @param featureName - Name used for RUM event and logging
 * @param customDomain - Custom domain name
 * @param rolloutPercent - Rollout percentage
 * @returns true if the custom domain should be used, false otherwise
 */
export function shouldUseCustomDomain(
  featureName: string,
  customDomain?: string,
  rolloutPercent?: number
): boolean {
  const userId = rumService.getUserId();
  const shouldUseCustomDomain =
    !!customDomain &&
    !!rolloutPercent &&
    shouldUseFeature(userId || '', rolloutPercent);

  rumService.recordEvent(`${featureName}_custom_domain_rollout`, {
    shouldUseCustomDomain: shouldUseCustomDomain,
    userId: userId,
    customDomain: customDomain,
    rolloutPercent: rolloutPercent,
  });

  logger.info(`Use ${featureName} custom domain: ${shouldUseCustomDomain}`);

  return shouldUseCustomDomain;
}

export function shouldUseIdpConfigCustomDomain(
  customDomain?: string,
  rolloutPercent?: number
): boolean {
  return shouldUseCustomDomain('idpconfig', customDomain, rolloutPercent);
}

export function shouldUseProfileCustomDomain(
  customDomain?: string,
  rolloutPercent?: number
): boolean {
  return shouldUseCustomDomain('profile', customDomain, rolloutPercent);
}
